import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { APP_VERSION } from '../../version';
import { api } from '../../services/api';
import { BuildInfo, FRONTEND_BUILD, popisBuildu } from '../../config/buildInfo';

/**
 * Patička aplikace (copyright + verze).
 *
 * Vykresluje se jednou v MainLayout pod obsahem stránky. Původní verze ji vkládala
 * do každé stránky zvlášť (21 souborů) – tady stačí jedno místo a nová stránka ji
 * dostane automaticky.
 *
 * Číslo verze je „naposledy oznámené vydání" a vede na novinky o aplikaci, ať se
 * uživatel dostane k tomu, co ve verzi přibylo.
 *
 * Build stamp frontendu i backendu (hash commitu, větev, čas sestavení) je
 * ZÁMĚRNĚ jen v HTML komentáři, ne v popisku u verze. Uživateli neříká nic a
 * testera zbytečně vyděsí; kdo ho potřebuje, najde ho v kódu stránky.
 */

/** Text komentáře do stránky. Bez „--" a špičatých závorek, ať komentář nerozbije. */
const bezpecnyKomentar = (radky: string[]): string =>
  `\n${radky.join('\n')}\n`.replace(/-{2,}/g, '-').replace(/[<>]/g, '');
export const AppFooter: React.FC = () => {
  const { data: backend } = useQuery({
    queryKey: ['app-info'],
    queryFn: async () =>
      (await api.get<{ verze: string | null; build: BuildInfo | null }>('/app-info')).data,
    // Mění se jen nasazením; žádné opakované dotazy během práce.
    staleTime: Infinity,
    retry: false,
  });

  const otisk = bezpecnyKomentar([
    `EFlyer ${APP_VERSION}`,
    `frontend: ${popisBuildu(FRONTEND_BUILD)}`,
    backend
      ? `backend: ${popisBuildu(backend.build)}${
          backend.verze && backend.verze !== APP_VERSION ? ` | verze ${backend.verze}` : ''
        }`
      : 'backend: nezjištěno',
  ]);

  // Rozdílná verze frontendu a backendu znamená, že se nasadila jen jedna strana.
  const nesouhlasiVerze = !!backend?.verze && backend.verze !== APP_VERSION;

  return (
    <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 pt-2 pb-4 border-t border-gray-200">
      <div className="flex justify-between items-center text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <span className="text-lg">©</span>
          <a
            href="https://netmate.cz"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-600 transition-colors"
          >
            NetMate CZ spol. s r.o.
          </a>
        </div>
        <div className="flex items-center">
          {/* Otisk sestavení – jen pro toho, kdo se podívá do kódu stránky. */}
          <span className="hidden" dangerouslySetInnerHTML={{ __html: `<!--${otisk}-->` }} />
          <Link
            to="/news?filtr=verze"
            className={`hover:text-blue-600 hover:underline transition-colors ${
              nesouhlasiVerze ? 'text-amber-600' : 'text-gray-400'
            }`}
            title={
              nesouhlasiVerze
                ? 'Co je ve verzi nového. Pozor: frontend a backend hlásí různou verzi – zřejmě se nasadila jen jedna strana.'
                : 'Co je ve verzi nového'
            }
          >
            Verze: {APP_VERSION}
            {nesouhlasiVerze && ' ⚠'}
          </Link>
        </div>
      </div>
    </footer>
  );
};
